import React, { createContext, useState, useContext, useEffect } from 'react';
import { useAuth } from './AuthContext';
import api from '../config/api';

const CartContext = createContext();

export const useCart = () => {
  const context = useContext(CartContext);
  if (!context) {
    throw new Error('useCart must be used within CartProvider');
  }
  return context;
};

export const CartProvider = ({ children }) => {
  const [cartItems, setCartItems] = useState([]);
  const [cartTotal, setCartTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const { user } = useAuth();

  useEffect(() => {
    if (user) {
      fetchCart();
    } else {
      // Load from localStorage for guest users
      const savedCart = localStorage.getItem('guestCart');
      if (savedCart) {
        try {
          setCartItems(JSON.parse(savedCart));
        } catch (error) {
          console.error('Failed to load guest cart:', error);
        }
      }
    }
  }, [user]);

  useEffect(() => {
    const total = cartItems.reduce((sum, item) => {
      const price = parseFloat(item.price || 0);
      const quantity = parseInt(item.quantity || 0);
      return sum + (price * quantity);
    }, 0);
    setCartTotal(total);
    
    // Save guest cart to localStorage
    if (!user) {
      localStorage.setItem('guestCart', JSON.stringify(cartItems));
    }
  }, [cartItems, user]);

  const fetchCart = async () => {
    if (!user) return;
    
    try {
      setLoading(true);
      const response = await api.get('/cart');
      setCartItems(response.data.items || []);
      setCartTotal(response.data.total || 0);
    } catch (error) {
      console.error('Failed to fetch cart:', error);
    } finally {
      setLoading(false);
    }
  };

  const addToCart = async (productId, quantity = 1) => {
    if (user) {
      try {
        await api.post('/cart/add', { product_id: productId, quantity });
        await fetchCart();
        return { success: true };
      } catch (error) {
        return {
          success: false,
          error: error.response?.data?.error || 'Failed to add to cart'
        };
      }
    } else {
      // Guest cart - fetch product details first
      try {
        // Fetch product by ID
        const productResponse = await api.get(`/products/id/${productId}`);
        const product = productResponse.data.product;
        
        // Check if item already exists in cart
        const existingItem = cartItems.find(item => item.product_id === productId);
        
        if (existingItem) {
          // Update quantity
          setCartItems(cartItems.map(item =>
            item.product_id === productId
              ? { ...item, quantity: item.quantity + quantity }
              : item
          ));
        } else {
          // Add new item
          const newItem = {
            id: Date.now(),
            product_id: productId,
            name: product.name,
            price: parseFloat(product.price),
            image_url: product.image_url,
            quantity,
            slug: product.slug
          };
          setCartItems([...cartItems, newItem]);
        }
        return { success: true };
      } catch (error) {
        // Fallback: try to get product by slug if ID doesn't work
        console.error('Failed to fetch product details:', error);
        return {
          success: false,
          error: 'Failed to add product to cart'
        };
      }
    }
  };

  const updateCartItem = async (itemId, quantity) => {
    if (user) {
      try {
        await api.put(`/cart/update/${itemId}`, { quantity });
        await fetchCart();
        return { success: true };
      } catch (error) {
        return {
          success: false,
          error: error.response?.data?.error || 'Failed to update cart'
        };
      }
    } else {
      setCartItems(cartItems.map(item =>
        item.id === itemId ? { ...item, quantity } : item
      ));
      return { success: true };
    }
  };

  const removeFromCart = async (itemId) => {
    if (user) {
      try {
        await api.delete(`/cart/remove/${itemId}`);
        await fetchCart();
        return { success: true };
      } catch (error) {
        return {
          success: false,
          error: error.response?.data?.error || 'Failed to remove from cart'
        };
      }
    } else {
      setCartItems(cartItems.filter(item => item.id !== itemId));
      return { success: true };
    }
  };

  const clearCart = async () => {
    if (user) {
      try {
        await api.delete('/cart/clear');
        setCartItems([]);
        setCartTotal(0);
        return { success: true };
      } catch (error) {
        return {
          success: false,
          error: error.response?.data?.error || 'Failed to clear cart'
        };
      }
    } else {
      setCartItems([]);
      localStorage.removeItem('guestCart');
      return { success: true };
    }
  };

  const getCartCount = () => {
    return cartItems.reduce((sum, item) => sum + item.quantity, 0);
  };

  return (
    <CartContext.Provider value={{
      cartItems,
      cartTotal,
      loading,
      addToCart,
      updateCartItem,
      removeFromCart,
      clearCart,
      getCartCount,
      fetchCart
    }}>
      {children}
    </CartContext.Provider>
  );
};

